import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Table, InputNumber, Alert, Typography, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { formatNumber } from '../../../utils/numberFormat';

const { Text } = Typography;

export interface AllocationPlanRow {
  instrumentId: string;
  exchange: string;
  symbol: string;
  proposed: string;
  price: number | null;
  lots: number | null;
}

export interface AllocationPlan {
  needsAllocation: boolean;
  cap: string | null;
  available: string | null;
  budget: string;
  rows: AllocationPlanRow[];
  warnings: string[];
}

interface Props {
  open: boolean;
  plan: AllocationPlan | null;
  placing: boolean;
  onCancel: () => void;
  onConfirm: (allocations: Record<string, number>) => void;
}

const fmt = (n: number): string => formatNumber(n);

/**
 * The one moment a grid user is asked about money.
 *
 * A grid's per-symbol wallet is funded by deposit, and a user who has never deposited gets a
 * bot with no capital: it starts, reports healthy, and never places an order. Nothing used to
 * say so. This offers the account's allocation up front, split evenly, and lets the user take
 * less before the bot goes anywhere near the market.
 *
 * Only ever shown while every wallet is still empty. After this the user moves money between
 * symbols with deposit and withdraw, and the limit stops applying.
 */
const FirstAllocationModal: React.FC<Props> = ({ open, plan, placing, onCancel, onConfirm }) => {
  const [amounts, setAmounts] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!open || !plan) return;
    const next: Record<string, number> = {};
    for (const row of plan.rows) next[row.instrumentId] = Number(row.proposed);
    setAmounts(next);
  }, [open, plan]);

  const budget = Number(plan?.budget ?? 0);
  const total = useMemo(
    () => Object.values(amounts).reduce((sum, v) => sum + (Number(v) || 0), 0),
    [amounts],
  );
  const over = total > budget;
  const nothing = total <= 0;

  const lotsFor = (row: AllocationPlanRow): number | null => {
    const price = row.price;
    if (price == null || price <= 0) return null;
    return Math.floor((amounts[row.instrumentId] ?? 0) / (price * 1000 * 100));
  };

  const columns: ColumnsType<AllocationPlanRow> = [
    {
      title: 'Symbol',
      dataIndex: 'instrumentId',
      render: (_: string, r) => <Text strong>{r.exchange}:{r.symbol}</Text>,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      width: 110,
      align: 'right',
      render: (v: number | null) =>
        v == null ? <Text type="secondary">n/a</Text> : `${fmt(v)}k`,
    },
    {
      title: 'Allocation (VND)',
      key: 'amount',
      width: 200,
      render: (_: unknown, r) => (
        <InputNumber
          style={{ width: '100%' }}
          min={0}
          step={1000000}
          value={amounts[r.instrumentId]}
          onChange={(v) =>
            setAmounts((prev) => ({ ...prev, [r.instrumentId]: Number(v ?? 0) }))
          }
        />
      ),
    },
    {
      title: 'Buys',
      key: 'lots',
      width: 120,
      align: 'right',
      render: (_: unknown, r) => {
        const lots = lotsFor(r);
        if (lots == null) return <Text type="secondary">unknown</Text>;
        if (lots === 0) return <Text type="danger">0 lots</Text>;
        return <Text>{lots} lots</Text>;
      },
    },
  ];

  const remaining = budget - total;

  return (
    <Modal
      open={open}
      title="Set the starting allocation"
      okText="Confirm and place"
      okButtonProps={{ disabled: over || nothing, loading: placing }}
      cancelButtonProps={{ disabled: placing }}
      onCancel={onCancel}
      onOk={() => onConfirm(amounts)}
      width={720}
      destroyOnClose
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Text type="secondary">
          This grid has no capital yet. Split the amount below across your symbols. You can
          take less than offered, and you can move money between symbols later with deposit
          and withdraw.
        </Text>

        {(plan?.warnings ?? []).map((w) => (
          <Alert key={w} type="warning" showIcon message={w} />
        ))}

        <Table<AllocationPlanRow>
          rowKey="instrumentId"
          size="small"
          pagination={false}
          columns={columns}
          dataSource={plan?.rows ?? []}
        />

        <div>
          <Text type="secondary">Allocated:</Text>{' '}
          <Text strong>{fmt(total)} VND</Text>
          <Text type="secondary"> of {fmt(budget)} available</Text>
          {!over && remaining > 0 && (
            <Text type="secondary">
              {'. '}
              {fmt(remaining)} VND stays uncommitted and can be deposited later.
            </Text>
          )}
        </div>

        {over && (
          <Alert
            type="error"
            showIcon
            message={`This is ${fmt(total - budget)} VND more than the account has available.`}
          />
        )}
        {nothing && !over && (
          <Alert type="warning" showIcon message="Give at least one symbol some capital." />
        )}
        <Alert
          type="info"
          showIcon
          message="A symbol that cannot buy one lot of 100 shares will never place an order."
        />
      </Space>
    </Modal>
  );
};

export default FirstAllocationModal;
